import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API from "../utils/api";
import WorkOrderMap from "../components/WorkOrderMap";
import {
  Status,
  StatusColor,
  Color,
  Role,
  ViewMode,
  EXPORT_ROLES,
} from "../constants";

// Not Completed is transient — orders auto-reopen to Pending, so none ever
// rest in this status. Excluded from the filter dropdown.
const STATUS_OPTIONS = Object.values(Status).filter(
  (s) => s !== Status.NOT_COMPLETED,
);

function StatusBadge({ status }) {
  const sc = StatusColor[status] || {
    bg: "#F3F4F6",
    text: "#666",
    dot: "#999",
  };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
      style={{ backgroundColor: sc.bg, color: sc.text }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: sc.dot }}
      />
      {status}
    </span>
  );
}

const fmtDate = (dateStr, withTime = false) => {
  if (!dateStr) return "—";
  const normalized =
    dateStr.endsWith("Z") || dateStr.includes("+") ? dateStr : dateStr + "Z";
  const d = new Date(normalized);
  const opts = { month: "short", day: "numeric", year: "numeric" };
  if (withTime)
    return d.toLocaleString("en-CA", {
      ...opts,
      hour: "2-digit",
      minute: "2-digit",
    });
  return d.toLocaleDateString("en-CA", opts);
};

// One labelled row inside the mobile filter sheet
function SheetField({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10.5px] font-bold text-gray-400 uppercase tracking-wider mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}

const sheetInput =
  "w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white " +
  "focus:outline-none focus:ring-2";

const toISO = (d) => d.toISOString().slice(0, 10);

// Quick "Created" range presets — weeks run Monday–Sunday.
const DATE_PRESETS = {
  "This week": () => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return [toISO(start), toISO(end)];
  },
  "Last week": () => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - ((now.getDay() + 6) % 7) - 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return [toISO(start), toISO(end)];
  },
  "This month": () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return [toISO(start), toISO(end)];
  },
  "Last month": () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return [toISO(start), toISO(end)];
  },
};

export default function WorkOrders() {
  const { user, isDelegated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(ViewMode.DEFAULT);
  const [showDeleted, setShowDeleted] = useState(false);

  // Filters
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("status") || "",
  );
  const [categoryFilter, setCategoryFilter] = useState(
    searchParams.get("category") || "",
  );
  const [routeFilter, setRouteFilter] = useState(
    searchParams.get("route") || "",
  );
  const [dsmFilter, setDsmFilter] = useState(searchParams.get("dsm") || "");
  const [dateFrom, setDateFrom] = useState(searchParams.get("from") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("to") || "");
  const [overdueOnly, setOverdueOnly] = useState(
    searchParams.get("overdue") === "1",
  );
  const [assignedToFilter, setAssignedToFilter] = useState(
    searchParams.get("assigned") || "",
  );
  const [createdByFilter, setCreatedByFilter] = useState(
    searchParams.get("createdBy") || "",
  );
  const [myOrders, setMyOrders] = useState(searchParams.get("mine") === "1");
  const [completedFrom, setCompletedFrom] = useState(
    searchParams.get("compFrom") || "",
  );
  const [completedTo, setCompletedTo] = useState(
    searchParams.get("compTo") || "",
  );
  const [dueFrom, setDueFrom] = useState(searchParams.get("dueFrom") || "");
  const [dueTo, setDueTo] = useState(searchParams.get("dueTo") || "");
  const [regionFilter, setRegionFilter] = useState(
    searchParams.get("region") || "",
  );
  const [cityFilter, setCityFilter] = useState(searchParams.get("city") || "");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [view, setView] = useState("table");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mapData, setMapData] = useState({ points: [], missing_location: 0 });
  const [mapLoading, setMapLoading] = useState(false);

  // Sort
  const [sortField, setSortField] = useState("desired_completion_date");
  const [sortDir, setSortDir] = useState("asc");

  // Lookup data
  const [categories, setCategories] = useState([]);
  const [dsms, setDsms] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [creators, setCreators] = useState([]);
  const [regions, setRegions] = useState([]);
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);

  const role = user?.role;
  // Only a delegated ASM still has a view toggle — DSMs now always see their
  // whole warehouse, and SA sees every warehouse.
  const showToggle = role === Role.ASM && isDelegated();
  const canExport = EXPORT_ROLES.includes(role);
  const isSuperAdmin = user?.is_super_admin;

  // Roles that see more than one region get a Region filter.
  const showRegionFilter =
    isSuperAdmin ||
    [Role.SUPER_ADMIN, Role.ADMIN, Role.GSM, Role.SA].includes(role) ||
    (role === Role.DSM && user?.is_admin);

  // A delegated ASM only ever sees one team, so a DSM filter is meaningless.
  const showDsmFilter = [
    Role.SUPER_ADMIN,
    Role.GSM,
    Role.ADMIN,
    Role.SA,
    Role.DSM,
    Role.ST,
  ].includes(role);

  const showRouteFilter =
    [Role.SUPER_ADMIN, Role.GSM, Role.ADMIN, Role.DSM, Role.ST].includes(
      role,
    ) ||
    (role === Role.ASM && isDelegated());

  const toggleOptions = [
    { value: ViewMode.DEFAULT, label: "My Route" },
    { value: ViewMode.TEAM, label: "DSM Team" },
  ];

  useEffect(() => {
    API.get("/lookup/categories")
      .then((r) => setCategories(r.data))
      .catch(() => {});
    // Only people who actually appear on orders this user can see
    API.get("/work-orders/filter-options", { params: { view_mode: viewMode } })
      .then((r) => {
        setAssignees(r.data.assignees || []);
        setCreators(r.data.creators || []);
      })
      .catch(() => {});
    if (showDsmFilter) {
      API.get("/lookup/dsms")
        .then((r) => setDsms(r.data))
        .catch(() => {});
    }
    if (showRegionFilter) {
      API.get("/lookup/regions")
        .then((r) => setRegions(r.data))
        .catch(() => {});
    }
  }, [showDsmFilter, showRegionFilter, viewMode]);

  // Single source of truth for the query — used by both the table fetch and
  // the Excel export so the two can never disagree.
  const buildParams = useCallback(
    () => ({
      view_mode: viewMode,
      ...(search && { search }),
      ...(statusFilter && { status: statusFilter }),
      ...(categoryFilter && { task_category: categoryFilter }),
      ...(routeFilter && { route: routeFilter }),
      ...(cityFilter && { city: cityFilter }),
      ...(dsmFilter && { dsm_id: dsmFilter }),
      ...(regionFilter && { region_id: regionFilter }),
      ...(assignedToFilter && { assigned_to_id: assignedToFilter }),
      ...(createdByFilter && { created_by_id: createdByFilter }),
      ...(myOrders && { my_orders: true }),
      ...(overdueOnly && { overdue_only: true }),
      ...(showDeleted && { include_deleted: true }),
      ...(dateFrom && { created_from: dateFrom }),
      ...(dateTo && { created_to: dateTo }),
      ...(completedFrom && { completed_from: completedFrom }),
      ...(completedTo && { completed_to: completedTo }),
      ...(dueFrom && { date_from: dueFrom }),
      ...(dueTo && { date_to: dueTo }),
    }),
    [
      viewMode,
      search,
      statusFilter,
      categoryFilter,
      routeFilter,
      cityFilter,
      dsmFilter,
      regionFilter,
      assignedToFilter,
      createdByFilter,
      myOrders,
      overdueOnly,
      showDeleted,
      dateFrom,
      dateTo,
      completedFrom,
      completedTo,
      dueFrom,
      dueTo,
    ],
  );

  const fetchOrders = useCallback(() => {
    setLoading(true);
    API.get("/work-orders", { params: { ...buildParams(), limit: 500 } })
      .then((res) => {
        setOrders(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => {
    const t = setTimeout(fetchOrders, search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrders]);

  // Long lists mean the filters scroll far out of reach, so offer a way back.
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // The map uses the same filters as the table, so switching views never
  // changes which orders you are looking at.
  useEffect(() => {
    if (view !== "map") return;
    setMapLoading(true);
    const t = setTimeout(() => {
      API.get("/work-orders/map", { params: buildParams() })
        .then((r) => setMapData(r.data))
        .catch(() => setMapData({ points: [], missing_location: 0 }))
        .finally(() => setMapLoading(false));
    }, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [view, buildParams, search]);

  // Route options come from an unfiltered fetch so selecting a route doesn't
  // shrink the list of routes you can pick from.
  useEffect(() => {
    API.get("/work-orders", { params: { view_mode: viewMode, limit: 500 } })
      .then((res) => {
        setAvailableRoutes(
          [...new Set(res.data.map((o) => o.route))].filter(Boolean).sort(),
        );
        setAvailableCities(
          [...new Set(res.data.map((o) => o.city))].filter(Boolean).sort(),
        );
      })
      .catch(() => {});
  }, [viewMode]);

  // All filtering is server-side now, so the API result is the final set.
  const sorted = [...orders].sort((a, b) => {
    let av = a[sortField] ?? "";
    let bv = b[sortField] ?? "";
    if (sortField.includes("date")) {
      av = av ? new Date(av) : new Date(0);
      bv = bv ? new Date(bv) : new Date(0);
    } else {
      av = String(av).toLowerCase();
      bv = String(bv).toLowerCase();
    }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setCategoryFilter("");
    setRouteFilter("");
    setCityFilter("");
    setDsmFilter("");
    setRegionFilter("");
    setDateFrom("");
    setDateTo("");
    setOverdueOnly(false);
    setAssignedToFilter("");
    setCreatedByFilter("");
    setMyOrders(false);
    setCompletedFrom("");
    setCompletedTo("");
    setDueFrom("");
    setDueTo("");
    setSearchParams({});
  };

  const hasFilters =
    search ||
    statusFilter ||
    categoryFilter ||
    routeFilter ||
    cityFilter ||
    dsmFilter ||
    regionFilter ||
    dateFrom ||
    dateTo ||
    overdueOnly ||
    assignedToFilter ||
    createdByFilter ||
    myOrders ||
    completedFrom ||
    completedTo ||
    dueFrom ||
    dueTo;

  // Count of active filters living in the advanced section — surfaced on the
  // "More filters" button so collapsed filters aren't silently applied.
  const activeFilterCount = [
    statusFilter,
    categoryFilter,
    regionFilter,
    dsmFilter,
    routeFilter,
    cityFilter,
    assignedToFilter,
    createdByFilter,
    dateFrom,
    dateTo,
    completedFrom,
    completedTo,
    dueFrom,
    dueTo,
    overdueOnly,
    myOrders,
    showDeleted,
  ].filter(Boolean).length;

  const advancedCount = [
    assignedToFilter,
    createdByFilter,
    dueFrom,
    dueTo,
  ].filter(Boolean).length;

  // Sync filters to URL so they survive navigation
  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    if (categoryFilter) params.category = categoryFilter;
    if (routeFilter) params.route = routeFilter;
    if (cityFilter) params.city = cityFilter;
    if (dsmFilter) params.dsm = dsmFilter;
    if (regionFilter) params.region = regionFilter;
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (overdueOnly) params.overdue = "1";
    if (assignedToFilter) params.assigned = assignedToFilter;
    if (createdByFilter) params.createdBy = createdByFilter;
    if (myOrders) params.mine = "1";
    if (completedFrom) params.compFrom = completedFrom;
    if (completedTo) params.compTo = completedTo;
    if (dueFrom) params.dueFrom = dueFrom;
    if (dueTo) params.dueTo = dueTo;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    statusFilter,
    categoryFilter,
    routeFilter,
    cityFilter,
    dsmFilter,
    regionFilter,
    dateFrom,
    dateTo,
    overdueOnly,
    assignedToFilter,
    createdByFilter,
    myOrders,
    completedFrom,
    completedTo,
    dueFrom,
    dueTo,
  ]);

  const handleExport = async () => {
    try {
      // Same params as the table fetch — export always matches what's on screen
      const params = new URLSearchParams(buildParams());
      const res = await API.get(`/work-orders/export?${params}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `work-orders-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Export failed. Please try again.");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field)
      return <span className="ml-1 text-gray-300">↕</span>;
    return <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Work Orders</h1>
          <p className="text-gray-500 text-sm mt-1">
            {sorted.length} order{sorted.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Table / Map — Map is a placeholder until the view is built */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setView("table")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium"
              style={
                view === "table"
                  ? { backgroundColor: Color.NAVY, color: "#fff" }
                  : { backgroundColor: "#fff", color: "#374151" }
              }
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              Table
            </button>
            <button
              onClick={() => setView("map")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium"
              style={
                view === "map"
                  ? { backgroundColor: Color.NAVY, color: "#fff" }
                  : { backgroundColor: "#fff", color: "#374151" }
              }
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                />
              </svg>
              Map
            </button>
          </div>
          {showToggle && (
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              {toggleOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setViewMode(opt.value)}
                  className="px-3 py-1.5 text-sm font-medium transition-colors"
                  style={
                    viewMode === opt.value
                      ? { backgroundColor: Color.NAVY, color: "#fff" }
                      : { backgroundColor: "#fff", color: "#374151" }
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          {canExport && (
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border
                         border-gray-300 text-sm font-medium text-gray-600
                         hover:bg-gray-50 transition"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Export
            </button>
          )}
        </div>
      </div>


      {/* Filters — mobile: compact search + Filters button */}
      <div className="md:hidden flex gap-2 items-center mb-4">
        <div className="relative flex-1">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0"
            />
          </svg>
          <input
            type="text"
            placeholder="Search orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg
                       text-sm bg-white focus:outline-none focus:ring-2"
          />
        </div>
        <button
          onClick={() => setMobileFiltersOpen(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border
                     text-sm font-semibold whitespace-nowrap"
          style={
            activeFilterCount
              ? { borderColor: Color.NAVY, color: Color.NAVY }
              : { borderColor: "#DDE1E7", color: "#444" }
          }
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M8 12h8M11 18h2"
            />
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span
              className="px-1.5 rounded-full text-[10px] font-extrabold"
              style={{ backgroundColor: Color.YELLOW, color: "#5A3800" }}
            >
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters — mobile bottom sheet */}
      {mobileFiltersOpen && (
        // Leaflet's own layers and controls sit at z-index 400-1000, so the
        // sheet has to clear that or the map paints straight over it.
        <div className="md:hidden fixed inset-0" style={{ zIndex: 2000 }}>
          <div
            onClick={() => setMobileFiltersOpen(false)}
            className="absolute inset-0"
            style={{ backgroundColor: "rgba(15,0,105,.38)" }}
          />
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl
                       max-h-[90%] overflow-y-auto shadow-2xl"
          >
            <div className="px-5 pt-4 pb-6">
              <div className="w-9 h-1 bg-gray-300 rounded mx-auto mb-4" />
              <div className="flex items-center justify-between mb-5">
                <span
                  className="text-lg font-bold"
                  style={{ color: Color.NAVY }}
                >
                  Filters
                </span>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 text-gray-500
                             flex items-center justify-center"
                >
                  ✕
                </button>
              </div>

              <SheetField label="Status">
                <select
                  className={sheetInput}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </SheetField>

              <SheetField label="Category">
                <select
                  className={sheetInput}
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </SheetField>

              {showRegionFilter && regions.length > 0 && (
                <SheetField label="Warehouse">
                  <select
                    className={sheetInput}
                    value={regionFilter}
                    onChange={(e) => setRegionFilter(e.target.value)}
                  >
                    <option value="">All Warehouses</option>
                    {regions.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </SheetField>
              )}

              {showDsmFilter && dsms.length > 0 && (
                <SheetField label="DSM">
                  <select
                    className={sheetInput}
                    value={dsmFilter}
                    onChange={(e) => setDsmFilter(e.target.value)}
                  >
                    <option value="">All DSMs</option>
                    {dsms.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </SheetField>
              )}

              {showRouteFilter && availableRoutes.length > 0 && (
                <SheetField label="Route">
                  <select
                    className={sheetInput}
                    value={routeFilter}
                    onChange={(e) => setRouteFilter(e.target.value)}
                  >
                    <option value="">All Routes</option>
                    {availableRoutes.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </SheetField>
              )}

              {availableCities.length > 0 && (
                <SheetField label="City">
                  <select
                    className={sheetInput}
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                  >
                    <option value="">All Cities</option>
                    {availableCities.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </SheetField>
              )}

              <label className="flex items-center gap-2.5 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded"
                  checked={overdueOnly}
                  onChange={(e) => setOverdueOnly(e.target.checked)}
                />
                <span className="text-sm text-gray-700 font-medium">
                  Overdue only
                </span>
              </label>

              <label className="flex items-center gap-2.5 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded"
                  checked={myOrders}
                  onChange={(e) => setMyOrders(e.target.checked)}
                />
                <span className="text-sm text-gray-700 font-medium">
                  My orders
                </span>
              </label>

              {isSuperAdmin && (
                <label className="flex items-center gap-2.5 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded"
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: Color.RED }}
                  >
                    Show deleted
                  </span>
                </label>
              )}

              <div className="h-px bg-gray-100 my-4" />

              <SheetField label="Created — quick select">
                {/* 2x2 grid: four presets wrap raggedly in a flex row */}
                <div className="grid grid-cols-2 gap-2">
                  {Object.keys(DATE_PRESETS).map((label) => {
                    const [from, to] = DATE_PRESETS[label]();
                    const active = dateFrom === from && dateTo === to;
                    return (
                      <button
                        key={label}
                        onClick={() => {
                          setDateFrom(from);
                          setDateTo(to);
                        }}
                        className="w-full py-2 rounded-full border text-xs font-semibold"
                        style={
                          active
                            ? {
                                backgroundColor: Color.NAVY,
                                borderColor: Color.NAVY,
                                color: "#fff",
                              }
                            : { borderColor: "#DDE1E7", color: "#4B5563" }
                        }
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </SheetField>

              <SheetField label="Created date range">
                <div className="grid grid-cols-[1fr_18px_1fr] items-center gap-1.5">
                  <input
                    type="date"
                    className={sheetInput}
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                  <span className="text-center text-gray-300">—</span>
                  <input
                    type="date"
                    className={sheetInput}
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </SheetField>

              <SheetField label="Completion date">
                <div className="grid grid-cols-[1fr_18px_1fr] items-center gap-1.5">
                  <input
                    type="date"
                    className={sheetInput}
                    value={completedFrom}
                    onChange={(e) => setCompletedFrom(e.target.value)}
                  />
                  <span className="text-center text-gray-300">—</span>
                  <input
                    type="date"
                    className={sheetInput}
                    value={completedTo}
                    onChange={(e) => setCompletedTo(e.target.value)}
                  />
                </div>
              </SheetField>

              <SheetField label="Due date">
                <div className="grid grid-cols-[1fr_18px_1fr] items-center gap-1.5">
                  <input
                    type="date"
                    className={sheetInput}
                    value={dueFrom}
                    onChange={(e) => setDueFrom(e.target.value)}
                  />
                  <span className="text-center text-gray-300">—</span>
                  <input
                    type="date"
                    className={sheetInput}
                    value={dueTo}
                    onChange={(e) => setDueTo(e.target.value)}
                  />
                </div>
              </SheetField>

              <SheetField label="Created by">
                <select
                  className={sheetInput}
                  value={createdByFilter}
                  onChange={(e) => setCreatedByFilter(e.target.value)}
                >
                  <option value="">Anyone</option>
                  {creators.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </SheetField>

              <SheetField label="Assigned to">
                <select
                  className={sheetInput}
                  value={assignedToFilter}
                  onChange={(e) => setAssignedToFilter(e.target.value)}
                >
                  <option value="">Anyone</option>
                  {assignees.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </SheetField>

              <div className="flex gap-2.5 mt-6">
                <button
                  onClick={clearFilters}
                  className="flex-1 py-3 rounded-lg border border-gray-300
                             text-sm font-semibold text-gray-600 bg-white"
                >
                  Reset all
                </button>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="flex-[2] py-3 rounded-lg text-sm font-bold text-white"
                  style={{ backgroundColor: Color.NAVY }}
                >
                  Show {sorted.length} order{sorted.length !== 1 ? "s" : ""}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters — desktop */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200
                      p-4 mb-5 space-y-3">
        {/* Row 1: search full width */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0"
            />
          </svg>
          <input
            type="text"
            placeholder="Search customer, order #, task, city, customer #..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg
                       text-sm focus:outline-none focus:ring-2"
          />
        </div>

        {/* Row 2: dropdowns + checkboxes + more/fewer filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>

          {showRegionFilter && regions.length > 0 && (
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2"
            >
              <option value="">All Warehouses</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}

          {showDsmFilter && dsms.length > 0 && (
            <select
              value={dsmFilter}
              onChange={(e) => setDsmFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2"
            >
              <option value="">All DSMs</option>
              {dsms.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          )}

          {showRouteFilter && availableRoutes.length > 0 && (
            <select
              value={routeFilter}
              onChange={(e) => setRouteFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2"
            >
              <option value="">All Routes</option>
              {availableRoutes.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          )}

          {availableCities.length > 0 && (
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2"
            >
              <option value="">All Cities</option>
              {availableCities.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => setOverdueOnly(e.target.checked)}
              className="rounded"
            />
            Overdue only
          </label>

          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={myOrders}
              onChange={(e) => setMyOrders(e.target.checked)}
              className="rounded"
            />
            My orders
          </label>

          <button
            onClick={() => setFiltersExpanded((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm
                       font-medium transition ml-auto"
            style={
              filtersExpanded || advancedCount
                ? { borderColor: Color.NAVY, color: Color.NAVY }
                : { borderColor: "#D1D5DB", color: "#4B5563" }
            }
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4h18M6 12h12M10 20h4"
              />
            </svg>
            {filtersExpanded ? "Fewer filters" : "More filters"}
            {!filtersExpanded && advancedCount > 0 && (
              <span
                className="ml-0.5 px-1.5 rounded-full text-xs font-semibold"
                style={{ backgroundColor: Color.YELLOW, color: "#4A3500" }}
              >
                {advancedCount}
              </span>
            )}
          </button>
        </div>

        {/* Row 3: created + completion date ranges */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500 whitespace-nowrap">
            Created
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2"
          />
          <span className="text-sm text-gray-500">—</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2"
          />

          <span className="text-sm text-gray-500 whitespace-nowrap ml-2">
            Completion
          </span>
          <input
            type="date"
            value={completedFrom}
            onChange={(e) => setCompletedFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2"
          />
          <span className="text-sm text-gray-500">—</span>
          <input
            type="date"
            value={completedTo}
            onChange={(e) => setCompletedTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2"
          />

          {isSuperAdmin && (
            <label
              className="flex items-center gap-2 text-sm cursor-pointer ml-2"
              style={{ color: Color.RED }}
            >
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="rounded"
              />
              Show deleted
            </label>
          )}

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-gray-400 hover:text-gray-600 underline ml-auto"
            >
              Clear all
            </button>
          )}
        </div>

        {/* Advanced filters */}
        {filtersExpanded && (
          <div className="pt-3 border-t border-gray-100 space-y-3">
            <p className="text-xs font-semibold text-gray-400 tracking-wide">
              ADVANCED FILTERS
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-500 whitespace-nowrap">
                Created
              </span>
              {Object.keys(DATE_PRESETS).map((label) => {
                const [from, to] = DATE_PRESETS[label]();
                const active = dateFrom === from && dateTo === to;
                return (
                  <button
                    key={label}
                    onClick={() => {
                      setDateFrom(from);
                      setDateTo(to);
                    }}
                    className="px-3 py-1.5 rounded-lg border text-sm font-medium transition"
                    style={
                      active
                        ? {
                            backgroundColor: Color.NAVY,
                            borderColor: Color.NAVY,
                            color: "#fff",
                          }
                        : { borderColor: "#D1D5DB", color: "#4B5563" }
                    }
                  >
                    {label}
                  </button>
                );
              })}

              <select
                value={createdByFilter}
                onChange={(e) => setCreatedByFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 ml-2"
              >
                <option value="">Created by</option>
                {creators.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>

              <select
                value={assignedToFilter}
                onChange={(e) => setAssignedToFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2"
              >
                <option value="">Assigned to</option>
                {assignees.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-500 whitespace-nowrap">
                Due date
              </span>
              <input
                type="date"
                value={dueFrom}
                onChange={(e) => setDueFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2"
              />
              <span className="text-sm text-gray-500">—</span>
              <input
                type="date"
                value={dueTo}
                onChange={(e) => setDueTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2"
              />
            </div>
          </div>
        )}
      </div>

      {/* Map */}
      {view === "map" &&
        (mapLoading ? (
          <div className="text-center py-16 text-gray-400">Loading map...</div>
        ) : (
          <WorkOrderMap
            points={mapData.points}
            missingLocation={mapData.missing_location}
            onSelect={(id) => navigate(`/work-orders/${id}`)}
          />
        ))}

      {/* Table */}
      {view === "table" &&
        (loading ? (
        <div className="text-center py-16 text-gray-400">Loading...</div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16">
          <svg
            className="w-12 h-12 mx-auto mb-3 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <p className="font-medium text-gray-500">No work orders found</p>
          <p className="text-sm text-gray-400 mt-1">
            Try adjusting your filters
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {[
                    {
                      field: "work_order_number",
                      label: "Order #",
                      sortable: false,
                    },
                    {
                      field: "customer_name",
                      label: "Customer",
                      sortable: true,
                    },
                    { field: "task_category", label: "Task", sortable: false },
                    { field: "status", label: "Status", sortable: true },
                    { field: "created_date", label: "Created", sortable: true },
                    {
                      field: "desired_completion_date",
                      label: "Due Date",
                      sortable: true,
                    },
                    {
                      field: "completed_date",
                      label: "Completion Date",
                      sortable: true,
                    },
                    {
                      field: "assigned_to",
                      label: "Assigned To",
                      sortable: false,
                    },
                  ].map((col) => (
                    <th
                      key={col.field}
                      onClick={() => col.sortable && handleSort(col.field)}
                      className={`px-4 py-3 text-left text-xs font-semibold text-gray-500
                                  uppercase tracking-wide
                                  ${col.sortable ? "cursor-pointer hover:text-gray-700 select-none" : ""}`}
                    >
                      {col.label}
                      {col.sortable && <SortIcon field={col.field} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((order) => {
                  const isOverdue = order.is_overdue;
                  const isDeleted = order.is_deleted;
                  return (
                    <tr
                      key={order.id}
                      onClick={() => navigate(`/work-orders/${order.id}`)}
                      className="cursor-pointer transition-colors border-b border-gray-50
                                 last:border-0 hover:brightness-95"
                      style={{
                        backgroundColor: isDeleted
                          ? "#F9FAFB"
                          : isOverdue
                            ? "#FFF0F0"
                            : "#FFFFFF",
                        opacity: isDeleted ? 0.6 : 1,
                      }}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">
                        <div>
                          {order.work_order_number || order.id.slice(0, 8)}
                        </div>
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {isDeleted && (
                            <span
                              className="px-1.5 py-0.5 rounded text-xs font-medium"
                              style={{
                                backgroundColor: "#FDECEA",
                                color: Color.RED,
                              }}
                            >
                              DELETED
                            </span>
                          )}
                          {order.attempt_count > 1 && (
                            <span
                              className="px-1.5 py-0.5 rounded text-xs font-medium"
                              style={{
                                backgroundColor: "#FFF8E1",
                                color: "#B38600",
                              }}
                            >
                              #{order.attempt_count}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">
                          {order.customer_name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {order.city} · {order.route}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {order.task_category}
                        {order.cooler_type && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            {order.cooler_type}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={order.status} />
                        {isOverdue && (
                          <p
                            className="text-xs mt-1 font-medium"
                            style={{ color: Color.RED }}
                          >
                            Overdue
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm">
                        {order.created_date ? (
                          <>
                            <div>{fmtDate(order.created_date)}</div>
                            {/* <div className="text-gray-400">{fmtDate(order.created_date, true).split(', ')[1]}</div> */}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm">
                        {order.desired_completion_date
                          ? new Date(
                              order.desired_completion_date + "T00:00:00",
                            ).toLocaleDateString("en-CA", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm">
                        {order.completed_date ? (
                          fmtDate(order.completed_date)
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {order.assigned_to || (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {sorted.map((order) => (
              <div
                key={order.id}
                onClick={() => navigate(`/work-orders/${order.id}`)}
                className="rounded-xl border cursor-pointer p-4 transition-colors"
                style={{
                  backgroundColor: order.is_overdue ? "#FFF0F0" : "#FFFFFF",
                  borderColor: order.is_overdue ? "#FFCDD2" : "#E5E7EB",
                }}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-medium text-gray-800 text-sm">
                      {order.customer_name}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {order.task_category}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {order.city ? `${order.city} · ` : ""}
                      {order.route}
                    </p>
                  </div>
                  <StatusBadge status={order.status} />
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                  <span>
                    Due:{" "}
                    {order.desired_completion_date
                      ? new Date(
                          order.desired_completion_date + "T00:00:00",
                        ).toLocaleDateString("en-CA", {
                          month: "short",
                          day: "numeric",
                        })
                      : "—"}
                  </span>
                  {order.assigned_to && <span>→ {order.assigned_to}</span>}
                  {order.is_overdue && (
                    <span className="font-medium" style={{ color: Color.RED }}>
                      Overdue
                    </span>
                  )}
                  {order.attempt_count > 1 && (
                    <span style={{ color: "#B38600" }}>
                      Attempt #{order.attempt_count}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
        ))}

      {/* Back to the filters at the top of a long list */}
      {showScrollTop && view === "table" && (
        <button
          onClick={() =>
            window.scrollTo({ top: 0, behavior: "smooth" })
          }
          aria-label="Back to top"
          className="fixed bottom-5 right-5 z-40 flex items-center gap-1.5
                     px-4 py-3 rounded-full shadow-lg text-sm font-semibold
                     text-white transition hover:brightness-110"
          style={{ backgroundColor: Color.NAVY }}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 15l7-7 7 7"
            />
          </svg>
          Filters
        </button>
      )}
    </div>
  );
}
